//src/pages/assetlist.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "./Navbar"; // Ensure correct path
import axios from "axios";
import * as XLSX from "xlsx";
import { Search, IosShare } from "@mui/icons-material";
import { InfoOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  TextField,
  IconButton,
} from "@mui/material";
import { NAVY, SLATE, GOLD, PANEL_BLUE_END, BG } from "../theme";

const AssetList = () => {
  const { category, type } = useParams(); // Extract parameters from URL
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]); // For search functionality
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [allowedModules, setAllowedModules] = useState([]);
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const rawbranches = localStorage.getItem("branchesAssigned");
    const storedAllowedModules = JSON.parse(
      localStorage.getItem("allowedModules") || "[]",
    );
    setAllowedModules(storedAllowedModules);

    const { API_CONFIG, REFRESH_CONFIG } = require("../configuration");

    axios
      .get(`${API_CONFIG.APIURL}/assetlist/details/${category}/${type}`, {
        params: {
          branchesAssigned: rawbranches,
          empid: userData?.emp_id,
        },
      })
      .then((response) => {
        setAssets(response.data);
        setFilteredAssets(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching asset details:", error);
        setError("Failed to load assets.");
        setLoading(false);
      });
  }, [category, type]);

  const handleAction = (assetId, action) => {
    const encodedAssetId = encodeURIComponent(assetId); // Encoding the assetId
    if (action === "Assign") {
      navigate(`/assign-asset/${encodedAssetId}`);
    } else if (action === "Free Pool") {
      navigate(`/free-asset/${encodedAssetId}`);
    } else if (action === "Maintenance") {
      navigate(`/undermaintenance-asset/${encodedAssetId}`);
    } else if (action === "Transfer") {
      navigate(`/transfer-asset/${encodedAssetId}`);
    }
  };

  // Function to handle search filtering
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = assets.filter(
      ({ asset_id, brand, model, imei_num, assignment_status, assigned_to }) =>
        [asset_id, brand, model, imei_num, assignment_status, assigned_to].some(
          (field) => field?.toString().toLowerCase().includes(query),
        ),
    );
    setFilteredAssets(filtered);
  };

  // Function to export data as Excel file
  const exportToExcel = () => {
    const formattedData = filteredAssets.map((asset) => ({
      "Asset ID": asset.asset_id,
      Name: `${asset.brand} - ${asset.model}`,
      "IMEI Number": asset.imei_num,
      Status: asset.assignment_status,
      "Emp ID": asset.assigned_to?.emp_id || "N/A",
      "Employee Name": asset.assigned_to?.emp_name || "N/A",
      "Employee email": asset.assigned_to?.emp_email || "N/A",
      "Employee Mobile": asset.assigned_to?.emp_mob || "N/A",
      "Employee Designation": asset.assigned_to?.designation_name || "N/A",
      Department: asset.assigned_to?.department_name || "N/A",
      "Reporting Manager": asset.assigned_to?.reporting_manager || "N/A",
      Branch: asset.assigned_to?.branchid_name || "N/A",
      Area: asset.assigned_to?.areaid_name || "N/A",
      Region: asset.assigned_to?.regionid_name || "N/A",
      Cluster: asset.assigned_to?.clusterid_name || "N/A",
      State: asset.assigned_to?.state || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Assets");
    XLSX.writeFile(workbook, `Asset_List_${category}_${type}.xlsx`);
  };

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress sx={{ color: SLATE }} />
      </Box>
    );

  if (error)
    return (
      <Typography color="error" textAlign="center" mt={3}>
        {error}
      </Typography>
    );

  const showActions = ["assigned", "free-pool", "maintenance"].includes(
    category,
  );
  const isApprovalModule = allowedModules.includes("IT-APPROVAL");
  const isAdmin = allowedModules.includes("IT-NEW ASSETS");

  // Status chip colors — tied to the theme triad instead of arbitrary MUI colors.
  // "Assigned" -> SLATE (identity), "Under Maintenance"/"In Progress" -> GOLD family
  // (attention-needed), everything else (Free Pool/available) -> success green.
  const statusStyles = {
    Assigned: { bg: `${SLATE}1F`, color: SLATE },
    "Under Maintenance": { bg: `${GOLD}26`, color: "#8A6A1E" },
    "In Progress": { bg: `${GOLD}3D`, color: "#7A5A16" },
    default: { bg: "#E3F5EC", color: "#1D9E75" },
  };
  const getStatusStyle = (status) => statusStyles[status] || statusStyles.default;

  return (
    <>
      <Navbar />
      <Box
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          ml: { xs: 0, md: "250px" }, // sidebar space
          width: {
            xs: "100%",
            md: "calc(100% - 250px)",
          },
          boxSizing: "border-box",
          overflowX: "hidden",
          backgroundColor: BG,
          minHeight: "100vh",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            fontWeight: "bold",
            textTransform: "capitalize",
            color: NAVY,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {category.replace("-", " ")} - {type}
          {/* Search & Export Buttons (Side by Side) */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Search Bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#FFFFFF",
                borderRadius: "30px",
                padding: "6px 12px",
                boxShadow: "0px 3px 8px rgba(4, 16, 33, 0.12)",
                width: "350px",
                border: "1px solid #ccc",
              }}
            >
              <Search sx={{ color: "#757575", marginRight: "8px" }} />
              <TextField
                placeholder="Search assets..."
                variant="standard"
                InputProps={{ disableUnderline: true }}
                value={searchQuery}
                onChange={handleSearch}
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Export Button (Next to Search Bar) */}
            <IconButton sx={{ color: SLATE }} onClick={exportToExcel}>
              <IosShare />
            </IconButton>
          </Box>
        </Typography>

        <TableContainer
          component={Paper}
          elevation={4}
          sx={{ borderRadius: 3 }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F4F6F8" }}>
                {[
                  "Asset ID",
                  "Name",
                  "IMEI Number",
                  "Status",
                  "Assigned To",
                  ...(showActions ? ["Actions"] : []),
                ].map((header) => (
                  <TableCell
                    key={header}
                    sx={{ fontWeight: "bold", color: NAVY }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAssets.map(
                ({
                  asset_id,
                  brand,
                  model,
                  imei_num,
                  assignment_status,
                  assigned_to,
                }) => {
                  const statusStyle = getStatusStyle(assignment_status);
                  return (
                    <TableRow key={asset_id} hover>
                      <TableCell>{asset_id}</TableCell>
                      <TableCell>{`${brand} - ${model}`}</TableCell>
                      <TableCell>{imei_num}</TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            padding: "4px 10px",
                            borderRadius: 1,
                            display: "inline-block",
                            fontSize: "0.85rem",
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                            fontWeight: 600,
                          }}
                        >
                          {assignment_status}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {assigned_to ? (
                          <>
                            {assigned_to.emp_id} - {assigned_to.emp_name}
                            <Tooltip
                              title={
                                <div
                                  style={{
                                    textAlign: "left",
                                    fontSize: "0.85rem",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  <strong>Emp ID:</strong> {assigned_to.emp_id}{" "}
                                  <br />
                                  <strong>Name:</strong> {assigned_to.emp_name}{" "}
                                  <br />
                                  <strong>Designation:</strong>{" "}
                                  {assigned_to.designation_name} <br />
                                  <strong>Department:</strong>{" "}
                                  {assigned_to.department_name} <br />
                                  <strong>Branch:</strong>{" "}
                                  {assigned_to.branchid_name} <br />
                                  <strong>Area:</strong>{" "}
                                  {assigned_to.areaid_name} <br />
                                  <strong>Region:</strong>{" "}
                                  {assigned_to.regionid_name} <br />
                                  <strong>Cluster:</strong>{" "}
                                  {assigned_to.clusterid_name} <br />
                                  <strong>State:</strong> {assigned_to.state}{" "}
                                  <br />
                                </div>
                              }
                              arrow
                            >
                              <IconButton
                                size="small"
                                sx={{ color: SLATE, ml: 1 }}
                              >
                                <InfoOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          "Unassigned"
                        )}
                      </TableCell>
                      {showActions && (
                        <TableCell>
                          {category === "assigned" && (
                            <Button
                              variant="contained"
                              sx={{
                                backgroundColor: "#1D9E75",
                                color: "white",
                                "&:hover": { backgroundColor: "#167A5A" },
                                mr: 1,
                                textTransform: "none",
                                borderRadius: 2,
                              }}
                              onClick={() => handleAction(asset_id, "Free Pool")}
                              disabled={
                                (isApprovalModule && !isAdmin) ||
                                assignment_status === "In Progress"
                              }
                            >
                              Free Pool
                            </Button>
                          )}
                          {category === "free-pool" && (
                            <Box display="flex" gap={2}>
                              <Button
                                variant="contained"
                                sx={{
                                  backgroundColor: SLATE,
                                  color: "white",
                                  "&:hover": { backgroundColor: NAVY },
                                  textTransform: "none",
                                  borderRadius: 2,
                                }}
                                onClick={() => handleAction(asset_id, "Assign")}
                                disabled={
                                  (isApprovalModule && !isAdmin) ||
                                  assignment_status === "In Progress"
                                }
                              >
                                Assign
                              </Button>
                              <Button
                                variant="contained"
                                sx={{
                                  backgroundColor: GOLD,
                                  color: "#111111",
                                  "&:hover": { backgroundColor: "#A97F2C" },
                                  textTransform: "none",
                                  borderRadius: 2,
                                }}
                                onClick={() =>
                                  handleAction(asset_id, "Maintenance")
                                }
                                disabled={
                                  (isApprovalModule && !isAdmin) ||
                                  assignment_status === "In Progress"
                                }
                              >
                                Maintenance
                              </Button>
                              <Button
                                variant="contained"
                                sx={{
                                  backgroundColor: PANEL_BLUE_END,
                                  color: "white",
                                  "&:hover": { backgroundColor: "#1B4F8C" },
                                  textTransform: "none",
                                  borderRadius: 2,
                                }}
                                onClick={() =>
                                  handleAction(asset_id, "Transfer")
                                }
                                disabled={
                                  (isApprovalModule && !isAdmin) ||
                                  assignment_status === "In Progress"
                                }
                              >
                                Transfer
                              </Button>
                            </Box>
                          )}

                          {category === "maintenance" && (
                            <Button
                              variant="contained"
                              sx={{
                                backgroundColor: "#1D9E75",
                                color: "white",
                                "&:hover": { backgroundColor: "#167A5A" },
                                textTransform: "none",
                                borderRadius: 2,
                              }}
                              onClick={() => handleAction(asset_id, "Free Pool")}
                              disabled={
                                (isApprovalModule && !isAdmin) ||
                                assignment_status === "In Progress"
                              }
                            >
                              Free Pool
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                },
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default AssetList;